/**
 * TD Product Card Custom Element
 * Handles scent swatch interactions, quick add functionality, and subscription selection
 */
if (!customElements.get('td-product-card')) {
  class TdProductCard extends HTMLElement {
    constructor() {
      super();
      this.scentSwatches = null;
      this.productImages = null;
      this.variantIdInput = null;
      this.priceContainers = null;
      this.subscriptionContainer = null;
      this.subscriptionCheckbox = null;
      this.sellingPlanInput = null;
      this.overlayButton = null;
      this.submitButton = null;
      this.loadingObserver = null;
      this.revealObserver = null;
      this.handleAnimationEnd = null;
    }

    connectedCallback() {
      // Handle reveal attribute removal
      this._initRevealHandling();

      // Cache element references
      this._cacheElements();

      // Initialize functionality based on what's available
      this._initScentSwatches();
      this._initQuickAddButton();
      this._initSubscriptionSelector();
    }

    disconnectedCallback() {
      // Clean up MutationObserver if it exists
      if (this.loadingObserver) {
        this.loadingObserver.disconnect();
        this.loadingObserver = null;
      }

      // Clean up IntersectionObserver if it exists
      if (this.revealObserver) {
        this.revealObserver.disconnect();
        this.revealObserver = null;
      }

      // Clean up animationend listener if it exists
      if (this.handleAnimationEnd) {
        this.removeEventListener('animationend', this.handleAnimationEnd);
        this.handleAnimationEnd = null;
      }
    }

    /**
     * Initialize reveal attribute handling
     * Removes reveal attribute when element becomes visible
     * Implements the same pattern as CustomHTMLElement.untilVisible()
     */
    _initRevealHandling() {
      if (!this.hasAttribute('reveal')) {
        return;
      }

      // If reduced motion is preferred, remove reveal immediately
      if (
        window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ) {
        this.removeAttribute('reveal');
        return;
      }

      // Function to handle element becoming visible - matches untilVisible() pattern
      const onBecameVisible = () => {
        this.classList.add('became-visible');
        this.style.opacity = '1';
        
        // Remove reveal attribute after transition completes
        // Wait for the CSS transition duration (0.5s) before removing
        setTimeout(() => {
          if (this.hasAttribute('reveal')) {
            this.removeAttribute('reveal');
          }
        }, 500);
      };

      // Use IntersectionObserver to detect when element becomes visible
      if (window.IntersectionObserver) {
        this.revealObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && entry.target === this) {
                this.revealObserver.disconnect();
                this.revealObserver = null;
                requestAnimationFrame(() => {
                  onBecameVisible();
                });
              }
            });
          },
          {
            rootMargin: '0px',
            threshold: 0.2,
          }
        );

        this.revealObserver.observe(this);
      } else {
        // Fallback for browsers without IntersectionObserver
        // Check if element is already visible
        const rect = this.getBoundingClientRect();
        const isVisible =
          rect.top < window.innerHeight &&
          rect.bottom > 0 &&
          rect.width > 0 &&
          rect.height > 0;

        if (isVisible) {
          onBecameVisible();
        } else {
          // Wait for element to become visible
          const checkVisibility = () => {
            const currentRect = this.getBoundingClientRect();
            const nowVisible =
              currentRect.top < window.innerHeight &&
              currentRect.bottom > 0 &&
              currentRect.width > 0 &&
              currentRect.height > 0;

            if (nowVisible) {
              onBecameVisible();
            } else {
              setTimeout(checkVisibility, 100);
            }
          };
          setTimeout(checkVisibility, 100);
        }
      }
    }

    /**
     * Cache frequently accessed DOM elements
     */
    _cacheElements() {
      this.scentSwatches = this.querySelectorAll('.td-product-card__scent-swatch');
      this.productImages = this.querySelectorAll('.td-product-card__primary-image');
      this.variantIdInput = this.querySelector('input[name="id"]');
      this.priceContainers = this.querySelectorAll('.td-product-card__price');
      this.variantTitleElement = this.querySelector('[data-variant-title]');
      this.subscriptionContainer = this.querySelector('.td-product-card__subscription');
      this.subscriptionCheckbox = this.subscriptionContainer
        ? this.subscriptionContainer.querySelector('.td-product-card__subscription-checkbox')
        : null;
      this.sellingPlanInput = this.querySelector('input[name="selling_plan"]');
      this.overlayButton = this.querySelector('.td-quick-add-button');
      this.submitButton = this.querySelector('.td-product-card__quick-add');
    }

    /**
     * Initialize scent swatch interaction handlers
     */
    _initScentSwatches() {
      if (!this.scentSwatches || this.scentSwatches.length === 0 || !this.variantIdInput) {
        if (this.variantTitleElement) {
          let activeSwatch = this.querySelector('.td-product-card__scent-swatch--active');
          const scentValue = activeSwatch?.getAttribute('data-scent-value');
          if (scentValue) {
            this.variantTitleElement.textContent = scentValue;
          } else {
            this.variantTitleElement.textContent = '';
          }
        }
        return;
      }

      // Initialize variant title from active swatch on load
      let activeSwatch = this.querySelector('.td-product-card__scent-swatch--active');
      
      // If no active swatch found by class, try to match by variant ID
      if (!activeSwatch && this.variantIdInput) {
        const currentVariantId = this.variantIdInput.value;
        if (currentVariantId) {
          activeSwatch = Array.from(this.scentSwatches).find(
            (swatch) => swatch.getAttribute('data-variant-id') === currentVariantId
          );
        }
      }
      
      if (activeSwatch && this.variantTitleElement) {
        const scentValue = activeSwatch.getAttribute('data-scent-value');
        if (scentValue) {
          this.variantTitleElement.textContent = scentValue;
        }
      }

      this.scentSwatches.forEach((swatch) => {
        swatch.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();

          const variantId = swatch.getAttribute('data-variant-id');
          const mediaId = swatch.getAttribute('data-variant-media-id');
          const variantPriceHtml = swatch.getAttribute('data-variant-price-html');
          const variantSubscriptionPriceHtml = swatch.getAttribute(
            'data-variant-subscription-price-html'
          );
          const scentValue = swatch.getAttribute('data-scent-value');

          if (!variantId) return;

          // Update active swatch
          this.scentSwatches.forEach((s) => {
            s.classList.remove('td-product-card__scent-swatch--active');
          });
          swatch.classList.add('td-product-card__scent-swatch--active');

          // Update variant ID in form
          this.variantIdInput.value = variantId;

          // Update product image
          this._updateProductImage(mediaId);

          // Update variant title with scent value
          if (this.variantTitleElement && scentValue) {
            this.variantTitleElement.textContent = scentValue;
          }

          // Update price based on subscription checkbox state
          this._updatePriceFromSwatch(
            variantPriceHtml,
            variantSubscriptionPriceHtml,
            swatch
          );
        });
      });
    }

    /**
     * Update product image based on media ID
     */
    _updateProductImage(mediaId) {
      if (!mediaId || !this.productImages || this.productImages.length === 0) {
        return;
      }

      this.productImages.forEach((img) => {
        const imgMediaId = img.getAttribute('data-media-id');
        if (imgMediaId === mediaId) {
          img.removeAttribute('hidden');
          img.classList.add('td-product-card__primary-image--active');
        } else {
          img.setAttribute('hidden', '');
          img.classList.remove('td-product-card__primary-image--active');
        }
      });
    }

    /**
     * Update price display from swatch click
     */
    _updatePriceFromSwatch(otpPriceHtml, subscriptionPriceHtml, swatch) {
      let priceToShow = otpPriceHtml;

      // Check if subscription is checked and use subscription price if available
      if (
        this.subscriptionCheckbox &&
        this.subscriptionCheckbox.checked &&
        subscriptionPriceHtml
      ) {
        priceToShow = subscriptionPriceHtml;
      }

      this._updatePriceDisplay(priceToShow);
    }

    /**
     * Initialize quick add button functionality
     */
    _initQuickAddButton() {
      if (!this.overlayButton || !this.submitButton) {
        return;
      }

      // Sync loading state from submit button to overlay button
      this._syncLoadingState();

      // Watch for aria-busy changes on the submit button
      this.loadingObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (
            mutation.type === 'attributes' &&
            mutation.attributeName === 'aria-busy'
          ) {
            this._syncLoadingState();
          }
        });
      });

      this.loadingObserver.observe(this.submitButton, {
        attributes: true,
        attributeFilter: ['aria-busy'],
      });

      // Handle click on overlay button
      this.overlayButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (this.submitButton) {
          this.submitButton.click();
        }
      });
    }

    /**
     * Sync loading state between submit button and overlay button
     */
    _syncLoadingState() {
      if (!this.submitButton || !this.overlayButton) {
        return;
      }

      const isBusy = this.submitButton.getAttribute('aria-busy') === 'true';
      if (isBusy) {
        this.overlayButton.setAttribute('aria-busy', 'true');
      } else {
        this.overlayButton.removeAttribute('aria-busy');
      }
    }

    /**
     * Initialize subscription selector functionality
     */
    _initSubscriptionSelector() {
      if (
        !this.subscriptionContainer ||
        !this.subscriptionCheckbox ||
        !this.sellingPlanInput
      ) {
        return;
      }

      // Get initial price data and plan ID
      const otpPriceHtml =
        this.subscriptionContainer.getAttribute('data-otp-price-html') || '';
      const subPriceHtml =
        this.subscriptionContainer.getAttribute('data-sub-price-html') || '';
      const basePlanId = this.subscriptionContainer.getAttribute('data-base-plan');

      // Update price display function - checks active swatch for variant-specific prices
      const updatePrice = (useSubscription) => {
        let priceHtml = '';

        // Get the currently active swatch dynamically
        const activeSwatch = this.querySelector('.td-product-card__scent-swatch--active');

        // If there's an active swatch, use its prices
        if (activeSwatch) {
          if (useSubscription) {
            const swatchSubscriptionPrice = activeSwatch.getAttribute(
              'data-variant-subscription-price-html'
            );
            if (swatchSubscriptionPrice) {
              priceHtml = swatchSubscriptionPrice;
            } else {
              // Fallback to container subscription price
              priceHtml = subPriceHtml;
            }
          } else {
            // Use swatch's one-time price
            const swatchOtpPrice = activeSwatch.getAttribute('data-variant-price-html');
            if (swatchOtpPrice) {
              priceHtml = swatchOtpPrice;
            } else {
              // Fallback to container one-time price
              priceHtml = otpPriceHtml;
            }
          }
        } else {
          // No active swatch, use container prices
          priceHtml = useSubscription ? subPriceHtml : otpPriceHtml;
        }

        this._updatePriceDisplay(priceHtml);
      };

      // Update subscription state
      const updateSubscriptionState = (isChecked, planId) => {
        if (isChecked && planId) {
          this.subscriptionContainer.classList.add('td-product-card__subscription--active');
          if (this.sellingPlanInput) {
            this.sellingPlanInput.value = planId;
          }
          updatePrice(true);
        } else {
          this.subscriptionContainer.classList.remove(
            'td-product-card__subscription--active'
          );
          if (this.sellingPlanInput) {
            this.sellingPlanInput.value = '';
          }
          updatePrice(false);
        }
      };

      // Handle checkbox change
      this.subscriptionCheckbox.addEventListener('change', (e) => {
        const isChecked = e.target.checked;
        updateSubscriptionState(isChecked, basePlanId);
      });

      // Initialize state based on checkbox
      updateSubscriptionState(this.subscriptionCheckbox.checked, basePlanId);
    }

    /**
     * Update price display in all price containers
     */
    _updatePriceDisplay(priceHtml) {
      if (!priceHtml || !this.priceContainers || this.priceContainers.length === 0) {
        return;
      }

      this.priceContainers.forEach((priceContainer) => {
        priceContainer.innerHTML = priceHtml;
      });
    }
  }

  customElements.define('td-product-card', TdProductCard);
}

